"use client";
import Link from "next/link";
import Image from "next/image";
import { Check } from "lucide-react";
import { useEffect, useRef } from "react";
import { gsap, ScrollTrigger } from "@/lib/gsap";

interface CollaborativeTechData {
  tagline: string;
  title: string;
  description: string;
  ctaButton?: {
    text: string;
    url: string;
  };
  mainImage: {
    src: string;
    alt: string;
  };
  upperImage: {
    src: string;
    alt: string;
  };
  smallImage: {
    src: string;
    alt: string;
  };
  features?: {
    text: string;
  }[];
}

interface CollaborativeTechClientProps {
  data: CollaborativeTechData;
  reverseLayout?: boolean;
}

export default function CollaborativeTechClient({
  data,
  reverseLayout = false,
}: CollaborativeTechClientProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const ctaButtonRef = useRef<HTMLAnchorElement>(null);
  const problemsListRef = useRef<HTMLUListElement>(null);
  const heroImageRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const bottomImageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!data) return;
    if (!sectionRef.current) return;
    const ctx = gsap.context(() => {
      const section = sectionRef.current;
      const label = labelRef.current;
      const heading = headingRef.current;
      const description = descriptionRef.current;
      const ctaButton = ctaButtonRef.current;
      const problemsList = problemsListRef.current;
      const heroImage = heroImageRef.current;
      const card = cardRef.current;
      const bottomImage = bottomImageRef.current;
      if (
        !section ||
        !label ||
        !heading ||
        !description ||
        !heroImage ||
        !card ||
        !bottomImage
      )
        return;

      gsap.set([label, heading, description], {
        opacity: 0,
        y: 40,
        filter: "blur(8px)",
      });

      if (ctaButton) {
        gsap.set(ctaButton, {
          opacity: 0,
          y: 30,
          scale: 0.9,
          filter: "blur(4px)",
        });
      }

      gsap.set([heroImage, card, bottomImage], {
        opacity: 0,
        y: 40,
        filter: "blur(8px)",
      });

      let problemItems: NodeListOf<Element> | null = null;
      if (problemsList) {
        problemItems = problemsList.querySelectorAll(
          '[data-animate="problem-item"]'
        );
        gsap.set(problemItems, {
          opacity: 0,
          x: -40,
          filter: "blur(8px)",
        });
      }

      const textTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top 75%",
          end: "top 25%",
          toggleActions: "play none none reverse",
          markers: false,
        },
      });

      textTimeline
        .to(label, {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: "power2.out",
          filter: "blur(0px)",
        })
        .to(
          heading,
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: "power2.out",
            filter: "blur(0px)",
          },
          "-=0.3"
        )
        .to(
          description,
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: "power2.out",
            filter: "blur(0px)",
          },
          "-=0.4"
        );

      if (ctaButton) {
        textTimeline.to(
          ctaButton,
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.6,
            ease: "power2.out",
            filter: "blur(0px)",
          },
          "-=0.2"
        );
      }

      if (problemItems && problemItems.length > 0) {
        const problemItemsTimeline = gsap.timeline({
          scrollTrigger: {
            trigger: problemItems,
            start: "top 100%",
            end: "top 40%",
            toggleActions: "play none none reverse",
            markers: false,
          },
        });

        problemItemsTimeline.to(problemItems, {
          opacity: 1,
          x: 0,
          duration: 0.8,
          stagger: 0.2,
          ease: "power2.out",
          filter: "blur(0px)",
        });
      }

      const isMobile = window.innerWidth <= 768;
      const imagesTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: isMobile ? heroImage : section,
          start: "top 75%",
          end: "top 20%",
          toggleActions: "play none none reverse",
          markers: false,
        },
      });

      imagesTimeline
        .to(
          heroImage,
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power2.out",
            filter: "blur(0px)",
          },
          0
        )
        .to(
          card,
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power2.out",
            filter: "blur(0px)",
          },
          0.2
        )
        .to(
          bottomImage,
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power2.out",
            filter: "blur(0px)",
          },
          0.4
        );
    }, sectionRef);

    const handleResize = () => {
      ScrollTrigger.refresh();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      ctx.revert();
      window.removeEventListener("resize", handleResize);
    };
  }, [
    sectionRef,
    labelRef,
    headingRef,
    descriptionRef,
    ctaButtonRef,
    problemsListRef,
    heroImageRef,
    cardRef,
    bottomImageRef,
    data,
  ]);

  if (!data) return null;

  const {
    tagline,
    title,
    description,
    ctaButton,
    mainImage,
    upperImage,
    smallImage,
    features,
  } = data;

  return (
    <section
      ref={sectionRef}
      id="collaborative-tech-section"
      className="py-10 md:py-[70px] bg-white"
    >
      <div className="container">
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-8 xl:gap-12 items-center">
          <div
            className={`flex flex-col gap-6 ${
              reverseLayout ? "lg:order-2" : "lg:order-1"
            }`}
          >
            <div className="max-lg:mb-6">
              {tagline && (
                <div
                  ref={labelRef}
                  className="flex items-center  mb-[14px] opacity-0"
                  data-animate="label"
                >
                  <div className="w-5 h-0.5 bg-theme-primary mr-3"></div>
                  <p className="text-xs font-semibold text-theme-primary tracking-wider uppercase">
                    {tagline}
                  </p>
                </div>
              )}

              {title && (
                <h2
                  ref={headingRef}
                  className="text-4xl md:text-5xl font-bold text-theme-secondary capitalize opacity-0"
                  data-animate="heading"
                >
                  {title}
                </h2>
              )}

              {description && (
                <p
                  ref={descriptionRef}
                  className="mt-6 text-lg text-theme-secondary-light opacity-0"
                  data-animate="description"
                >
                  {description}
                </p>
              )}

              {ctaButton && ctaButton.text && ctaButton.url && (
                <Link
                  ref={ctaButtonRef}
                  href={`/success-stories${ctaButton.url}`}
                  className="btn btn-primary btn-primary-hover-effect mt-6 opacity-0 cursor-pointer transition-all duration-300 relative overflow-hidden group before:content-[''] before:absolute before:top-0 before:left-[-100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:transition-[left] before:duration-500 hover:before:left-[100%]"
                  data-animate="cta-button"
                >
                  <span className="relative z-10 transition-all duration-300 text-white group-hover:text-white">
                    {ctaButton.text}
                  </span>
                </Link>
              )}

              {features && features.length > 0 && (
                <ul
                  ref={problemsListRef}
                  className="flex flex-col gap-[16px] mt-8 text-lg leading-[28px] font-medium text-theme-secondary "
                >
                  {features.map((feature, index) => (
                    <li
                      key={index}
                      data-animate="problem-item"
                      className="flex items-center gap-[16px]"
                    >
                      <div className="size-6 bg-[#F6D1B6] rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="size-4 text-[#B44E00]" />
                      </div>
                      {feature.text}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div
            className={`grid grid-cols-3 gap-4 sm:h-[400px] lg:h-auto max-lg:mb-6 ${
              reverseLayout ? "lg:order-1" : "lg:order-2"
            }`}
          >
            {mainImage && mainImage.src && (
              <div
                className={`col-span-3 sm:col-span-2 ${
                  reverseLayout ? "sm:order-2" : "sm:order-1"
                }`}
              >
                <div
                  ref={heroImageRef}
                  className="relative w-full h-[300px] sm:h-[400px] lg:h-full lg:max-h-[530px] rounded-xl overflow-hidden group opacity-0"
                  data-animate="hero-image"
                >
                  <Image
                    src={mainImage.src}
                    alt={mainImage.alt || "Main image"}
                    width={390}
                    height={520}
                    quality={85}
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 390px"
                    className="object-cover !size-full"
                  />
                </div>
              </div>
            )}

            <div
              className={` col-span-3 sm:col-span-1 flex flex-col gap-4 justify-between max-sm:hidden ${
                reverseLayout ? "sm:order-1" : "sm:order-2"
              }`}
            >
              {upperImage && upperImage.src && (
                <div
                  ref={cardRef}
                  className=" relative w-full h-full max-lg:max-h-[210px] lg:h-[274px] rounded-xl opacity-0 overflow-hidden"
                  data-animate="card"
                >
                  <Image
                    src={upperImage.src}
                    alt={upperImage.alt || "Upper image"}
                    width={184}
                    height={274}
                    quality={85}
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 30vw, 184px"
                    className="object-cover !size-full rounded-xl"
                  />
                </div>
              )}

              {smallImage && smallImage.src && (
                <div
                  ref={bottomImageRef}
                  className="relative w-full min-h-[175px] max-lg:max-h-[175px] lg:h-[230px] rounded-xl opacity-0"
                  data-animate="bottom-image"
                >
                  <Image
                    src={smallImage.src}
                    alt={smallImage.alt || "Small image"}
                    width={184}
                    height={230}
                    quality={85}
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 30vw, 184px"
                    className="object-cover !size-full rounded-xl"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
